import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowDownCircle, CheckCircle2, ChevronLeft, ChevronRight, Clock3, RefreshCw, ReceiptText, Wallet as WalletIcon, XCircle } from "lucide-react";
import api from "../lib/api";
import { gameLoadFailureMessage } from "../lib/walletMessages";
import { useAuth } from "../context/AuthContext";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import WalletHeroCard from "../components/WalletHeroCard";
import { useTheme } from "../context/ThemeContext";

const AUTO_CHECKOUT_PROVIDERS = new Set(["keyhub", "nexa", "nowpayments", "xxpay", "playhub"]);
const HISTORY_PAGE_SIZE = 20;
const REAL_DEPOSIT_PROVIDERS = new Set(["keyhub", "nowpayments", "xxpay", "playhub"]);
const REAL_DEPOSIT_METHOD_KEYS = [
  "cashapp",
  "chime",
  "card",
  "creditcard",
  "debitcard",
  "applepay",
  "googlepay",
  "crypto",
  "nowpayments",
  "bitcoin",
  "btc",
  "ethereum",
  "eth",
  "usdt",
  "usdttrc20",
  "usdterc20",
  "usdc",
  "litecoin",
  "ltc",
  "doge",
  "xxpay",
  "xxpayus",
  "playhub",
];

function compactPaymentText(value) {
  return String(value || "").toLowerCase().replace(/[^a-z0-9]+/g, "");
}

export default function WalletPage() {
  const { user, refreshUser } = useAuth();
  const { isDark } = useTheme();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const historyRef = useRef(null);
  const userReady = Boolean(user && user !== false);

  const load = useCallback(async () => {
    if (!userReady) return;
    setLoading(true);
    try {
      const reqRes = await api.get("/me/requests");
      setRequests(reqRes.data || []);
    } finally {
      setLoading(false);
    }
  }, [userReady]);

  useEffect(() => {
    load().catch(() => {});
  }, [load]);

  const visibleRequests = useMemo(() => {
    return requests.filter((row) => !isPassiveCheckoutPending(row));
  }, [requests]);

  const historyPages = Math.max(1, Math.ceil(visibleRequests.length / HISTORY_PAGE_SIZE));
  const currentHistoryPage = Math.min(historyPage, historyPages);
  const historyStart = (currentHistoryPage - 1) * HISTORY_PAGE_SIZE;
  const pageRequests = visibleRequests.slice(historyStart, historyStart + HISTORY_PAGE_SIZE);

  useEffect(() => {
    setHistoryPage((page) => Math.min(page, historyPages));
  }, [historyPages]);

  function changeHistoryPage(page) {
    setHistoryPage(Math.max(1, Math.min(page, historyPages)));
    requestAnimationFrame(() => historyRef.current?.scrollIntoView({ block: "start", behavior: "instant" }));
  }

  const totals = useMemo(() => {
    return visibleRequests.reduce((acc, row) => {
      if (row.status === "pending") acc.pending += 1;
      if (row.status === "approved" && isWalletDepositTotalRow(row)) acc.in += walletTransactionAmount(row);
      if (row.status === "approved" && isWalletWithdrawalTotalRow(row)) acc.out += walletTransactionAmount(row);
      return acc;
    }, { in: 0, out: 0, pending: 0 });
  }, [visibleRequests]);

  if (!user) {
    return (
      <div className={isDark ? "min-h-screen bg-[#05070D] text-white" : "min-h-screen bg-slate-50 text-slate-950"}>
        <Navbar />
        <main className="mx-auto max-w-md px-6 py-32 text-center">
          <h1 className="font-display text-3xl font-bold">Sign in to open your wallet</h1>
          <Link to="/login" className="mt-6 inline-flex rounded-full bg-[#2F6FA5] px-8 py-3 font-bold text-white">Sign in</Link>
        </main>
      </div>
    );
  }

  return (
    <div className={isDark ? "min-h-screen overflow-x-clip bg-[#05070D] text-white" : "min-h-screen overflow-x-clip bg-slate-50 text-slate-950"}>
      <Navbar />
      <main className="mx-auto w-full max-w-7xl px-4 pb-32 pt-8 sm:px-6 md:px-10">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#2F6FA5]">Wallet</p>
            <h1 className="font-display text-3xl font-black md:text-4xl">Manage your money</h1>
          </div>
          <button
            type="button"
            onClick={() => {
              refreshUser?.();
              load().catch(() => {});
            }}
            disabled={loading}
            className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-bold shadow-sm disabled:opacity-50 ${
              isDark ? "border-white/10 bg-[#081421] text-white hover:bg-[#0B2034]" : "border-slate-200 bg-white hover:bg-slate-50"
            }`}
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>

        <WalletHeroCard
          user={user}
          className="mb-6"
          onBalanceChange={() => {
            refreshUser?.();
            load().catch(() => {});
          }}
        />

        <section className="mb-6 grid gap-3 md:grid-cols-3">
          <Stat icon={WalletIcon} label="Approved added" value={money(totals.in)} tone="emerald" dark={isDark} />
          <Stat icon={ArrowDownCircle} label="Approved withdrawn" value={money(totals.out)} tone="amber" dark={isDark} />
          <Stat icon={Clock3} label="Pending reviews" value={totals.pending.toLocaleString()} tone="slate" dark={isDark} />
        </section>

        <section className="grid gap-6">
          <div ref={historyRef} data-testid="wallet-history" className={`scroll-mt-24 rounded-2xl border p-5 shadow-sm ${
            isDark ? "border-white/10 bg-[#081421]" : "border-slate-200 bg-white"
          }`}>
            <div className="mb-4 flex items-center gap-2">
              <ReceiptText className="h-4 w-4 text-[#2F6FA5]" />
              <h2 className="font-display text-xl font-bold">Wallet history</h2>
            </div>
            {historyPages > 1 && (
              <HistoryPagination page={currentHistoryPage} total={visibleRequests.length} onPage={changeHistoryPage} loading={loading} dark={isDark} position="top" />
            )}
            <div className="space-y-3">
              {pageRequests.map((row) => (
                <TransactionRow key={row.id} row={row} dark={isDark} />
              ))}
              {visibleRequests.length === 0 && (
                <div className="rounded-xl border border-dashed border-slate-200 p-8 text-center text-sm text-slate-400">
                  No wallet activity yet.
                </div>
              )}
            </div>
            {historyPages > 1 && (
              <HistoryPagination page={currentHistoryPage} total={visibleRequests.length} onPage={changeHistoryPage} loading={loading} dark={isDark} position="bottom" />
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

function HistoryPagination({ page, total, onPage, loading, dark, position }) {
  const pages = Math.ceil(total / HISTORY_PAGE_SIZE);
  const start = (page - 1) * HISTORY_PAGE_SIZE + 1;
  const end = Math.min(page * HISTORY_PAGE_SIZE, total);
  const buttonClass = `grid h-10 w-10 shrink-0 place-items-center rounded-lg border disabled:cursor-not-allowed disabled:opacity-40 ${
    dark ? "border-white/10 hover:bg-white/5" : "border-slate-200 hover:bg-slate-50"
  }`;
  return (
    <nav aria-label={`Wallet history pages (${position})`} className={`flex flex-wrap items-center justify-between gap-3 text-sm ${position === "top" ? "mb-4" : "mt-4"}`}>
      <span className={dark ? "text-slate-400" : "text-slate-500"}>{start}-{end} of {total}</span>
      <div className="flex items-center gap-2">
        <button type="button" aria-label="Previous history page" title="Previous page" className={buttonClass} disabled={loading || page <= 1} onClick={() => onPage(page - 1)}>
          <ChevronLeft aria-hidden="true" className="h-4 w-4" />
        </button>
        <span aria-live={position === "top" ? "polite" : "off"} className="min-w-[6rem] text-center tabular-nums">Page {page} of {pages}</span>
        <button type="button" aria-label="Next history page" title="Next page" className={buttonClass} disabled={loading || page >= pages} onClick={() => onPage(page + 1)}>
          <ChevronRight aria-hidden="true" className="h-4 w-4" />
        </button>
      </div>
    </nav>
  );
}

function TransactionRow({ row, dark }) {
  const isOut = isWalletHistoryOutRow(row);
  const Icon = row.status === "approved" ? CheckCircle2 : row.status === "rejected" ? XCircle : Clock3;
  return (
    <div data-testid="wallet-history-row" data-request-id={row.id} className={`w-full max-w-full overflow-hidden rounded-xl border p-4 shadow-sm ${
      dark ? "border-white/10 bg-[#050B14]" : "border-slate-200 bg-white"
    }`}>
      <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="min-w-0 break-words font-bold capitalize">{labelType(row)}</span>
            <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs ${statusTone(row.status)}`}>
              <Icon className="h-3 w-3" />
              {row.status}
            </span>
          </div>
          <p className={`mt-1 break-words text-sm ${dark ? "text-slate-300" : "text-slate-500"}`}>{walletNote(row)}</p>
          <p className="mt-2 text-xs text-slate-400">{row.created_at ? new Date(row.created_at).toLocaleString() : ""}</p>
        </div>
        <div className={`w-full min-w-0 text-left font-display text-lg font-black tabular-nums sm:w-auto sm:shrink-0 sm:text-right sm:text-xl ${isOut ? "text-amber-300" : "text-emerald-300"}`}>
          {isOut ? "-" : "+"}{money(walletTransactionAmount(row))}
        </div>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, tone, dark }) {
  const tones = dark ? {
    emerald: "border-emerald-400/20 text-emerald-200 bg-emerald-500/10",
    amber: "border-amber-400/20 text-amber-200 bg-amber-500/10",
    slate: "border-white/10 text-slate-100 bg-[#081421]",
  } : {
    emerald: "border-emerald-200 text-emerald-700 bg-emerald-50",
    amber: "border-amber-200 text-amber-700 bg-amber-50",
    slate: "border-slate-200 text-slate-700 bg-white",
  };
  return (
    <div className={`rounded-2xl border p-5 shadow-sm ${tones[tone]}`}>
      <Icon className="mb-3 h-5 w-5" />
      <div className="text-xs uppercase tracking-[0.18em] text-slate-500">{label}</div>
      <div className="mt-1 font-display text-2xl font-black tabular-nums">{value}</div>
    </div>
  );
}

function labelType(row = {}) {
  if (row.transfer_kind === "referral_wallet_credit") return "Referral wallet bonus";
  if (row.transfer_kind === "referral_freeplay_wallet_deposit") return "Referral freeplay";
  if (row.transfer_kind === "game_load" && row.status === "rejected") return row.game_id ? `Game load failed: ${displayGameName(row.game_id)}` : "Game load failed";
  if (row.transfer_kind === "game_load") return row.game_id ? `Game load: ${displayGameName(row.game_id)}` : "Game load";
  if (row.transfer_kind === "game_redeem") return row.game_id ? `Game redeem: ${displayGameName(row.game_id)}` : "Game redeem";
  if (row.transfer_kind === "phone_verification_freeplay" || row.type === "phone_verification_bonus") return "Phone verification freeplay";
  if (row.transfer_kind === "weekly_leaderboard_freeplay") return "Leaderboard freeplay";
  if (row.transfer_kind === "referral_freeplay") return "Referral freeplay";
  if (row.transfer_kind === "birthday_freeplay" || row.type === "birthday_bonus") return "Birthday freeplay";
  if (row.transfer_kind === "daily_spin_freeplay" || row.type === "daily_spin_reward") return "Daily spin freeplay";
  if (row.transfer_kind === "play_streak_freeplay" || row.type === "play_streak_reward") return "Play streak freeplay";
  if (row.transfer_kind === "plinko_play" || row.type === "plinko_play") return "Plinko play";
  if (row.transfer_kind === "crash_play" || row.type === "crash_play") return "Crash play";
  const type = row.type || "";
  const labels = {
    wallet_transfer_sent: "Transfer sent",
    wallet_transfer_received: "Transfer received",
    admin_credit: "Wallet credit",
    admin_debit: "Wallet debit",
    inactivity_wallet_expiry: "Wallet expired: 15-day inactivity",
    inactivity_freeplay_expiry: "Freeplay expired: 15-day inactivity",
  };
  return labels[type] || type.replace(/_/g, " ");
}

function walletNote(row) {
  if (row?.transfer_kind === "game_load" && row?.status === "rejected") {
    return gameLoadFailureMessage(row);
  }
  const text = row.note || row.admin_note || "No note";
  return String(text)
    .replace(/\bkey\s*hub\b/gi, "checkout")
    .replace(/\bkeyhub\b/gi, "checkout")
    .replace(/\s+/g, " ")
    .trim();
}

function isPassiveCheckoutPending(row) {
  if (!row || row.type !== "load" || row.status !== "pending") return false;
  const provider = String(row.payment_provider || "").trim().toLowerCase();
  if (!AUTO_CHECKOUT_PROVIDERS.has(provider)) return false;
  if (row.payment_mismatch) return false;
  const providerStatus = String(row.provider_status || "").trim().toLowerCase();
  if (["manual_review", "review_required", "requires_review"].includes(providerStatus)) return false;
  const adminNote = String(row.admin_note || "").toLowerCase();
  return !adminNote.includes("manual review") && !adminNote.includes("requires review");
}

function isGameLoadRow(row) {
  return row?.transfer_kind === "game_load"
    || (row?.type === "load" && Boolean(row?.game_id))
    || Number(row?.wallet_debit_amount || 0) > 0
    || Number(row?.free_play_debit_amount || 0) > 0
    || Number(row?.cash_wallet_debit_amount || 0) > 0;
}

function isWalletDepositTotalRow(row) {
  if (!row) return false;
  if (row.type !== "load") return false;
  if (isGameLoadRow(row)) return false;
  return isRealMoneyDepositMethod(row);
}

function isWalletWithdrawalTotalRow(row) {
  if (!row) return false;
  if (["admin_debit", "wallet_transfer_sent"].includes(row.type)) return true;
  if (row.type !== "redeem") return false;
  return row.transfer_kind !== "game_redeem" && !row.game_id;
}

function isWalletHistoryOutRow(row) {
  if (row?.transfer_kind === "plinko_play" || row?.type === "plinko_play") {
    return Number(row?.plinko_net_amount || 0) < 0;
  }
  if (row?.transfer_kind === "crash_play" || row?.type === "crash_play") {
    return Number(row?.crash_net_amount || 0) < 0;
  }
  return (isGameLoadRow(row) && row?.status !== "rejected")
    || ["redeem", "admin_debit", "wallet_transfer_sent", "inactivity_wallet_expiry", "inactivity_freeplay_expiry"].includes(row?.type);
}

function walletTransactionAmount(row) {
  if (row?.transfer_kind === "plinko_play" || row?.type === "plinko_play") return Math.abs(Number(row.plinko_net_amount || row.amount || 0));
  if (row?.transfer_kind === "crash_play" || row?.type === "crash_play") return Math.abs(Number(row.crash_net_amount || row.amount || 0));
  if (row?.transfer_kind === "game_redeem") return Number(row.wallet_credit_amount || row.amount || 0);
  if (row?.transfer_kind === "game_load" && row?.status === "rejected") return 0;
  if (row?.transfer_kind === "game_load") return Number(row.amount || row.wallet_debit_amount || row.free_play_debit_amount || 0);
  return Number(row?.amount || 0);
}

function isRealMoneyDepositMethod(row = {}) {
  const provider = compactPaymentText(row.payment_provider);
  const platform = compactPaymentText(row.payment_platform);
  const methodId = compactPaymentText(row.payment_method_id);
  if (REAL_DEPOSIT_PROVIDERS.has(provider)) return true;
  if (REAL_DEPOSIT_METHOD_KEYS.includes(platform) || REAL_DEPOSIT_METHOD_KEYS.includes(methodId)) return true;
  const haystack = compactPaymentText([
    row.note,
    row.admin_note,
    row.payment_provider,
    row.payment_platform,
    row.payment_method_id,
    row.gateway_transaction_id,
  ].filter(Boolean).join(" "));
  return REAL_DEPOSIT_METHOD_KEYS.some((key) => haystack.includes(key));
}

function displayGameName(value) {
  return String(value || "game")
    .replace(/[-_]+/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function statusTone(status) {
  if (status === "approved") return "border-emerald-200 bg-emerald-50 text-emerald-700";
  if (status === "rejected") return "border-red-200 bg-red-50 text-red-700";
  return "border-slate-200 bg-slate-50 text-slate-600";
}

function money(coins) {
  return `$${((coins || 0) / 1000).toFixed(2)}`;
}
